import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  LogOut, 
  LogIn, 
  RotateCcw, 
  Briefcase, 
  Users, 
  Clock, 
  Camera,
  RefreshCw,
  Building2,
  MapPin,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Home
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/employee-movements`;

// ============= CAMERA COMPONENT =============
const CameraCapture = ({ onCapture, capturedImage, onRetake }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  }, [stream]);

  useEffect(() => {
    if (!capturedImage) {
      startCamera();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.7);
    onCapture(imageData);
    stopCamera();
  };

  const handleRetake = () => {
    onRetake();
    startCamera();
  };

  if (error) {
    return (
      <div className="text-center p-4 bg-red-50 border border-red-200 rounded-lg">
        <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-600 text-sm">{error}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={startCamera}>
          Try Again
        </Button>
      </div>
    );
  }

  if (capturedImage) {
    return (
      <div className="space-y-2">
        <div className="relative rounded-lg overflow-hidden border-2 border-green-500">
          <img src={capturedImage} alt="Captured selfie" className="w-full" />
          <div className="absolute top-2 right-2">
            <CheckCircle className="w-6 h-6 text-green-500 bg-white rounded-full" />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRetake} className="w-full">
          <Camera className="w-4 h-4 mr-2" /> Retake Photo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative rounded-lg overflow-hidden bg-gray-900">
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          muted
          className="w-full"
        />
        {!cameraActive && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-white text-center">
              <Camera className="w-8 h-8 mx-auto mb-2 animate-pulse" />
              <p className="text-sm">Starting camera...</p>
            </div>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
      <Button 
        onClick={capturePhoto} 
        className="w-full bg-blue-600 hover:bg-blue-700"
        disabled={!cameraActive}
      >
        <Camera className="w-4 h-4 mr-2" /> Capture Selfie
      </Button>
    </div>
  );
};

// ============= STATUS BADGE COMPONENT =============
const StatusBadge = ({ status }) => {
  const statusConfig = {
    out: { label: 'Out', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    completed: { label: 'Completed', className: 'bg-green-100 text-green-800 border-green-300' },
    midway_returned: { label: 'Midway Return', className: 'bg-orange-100 text-orange-800 border-orange-300' },
    auto_closed: { label: 'Auto Closed', className: 'bg-gray-100 text-gray-800 border-gray-300' }
  };
  
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100' };
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

// ============= FORMAT TIME HELPER =============
const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  } catch {
    return '-';
  }
};

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return '-';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours}h ${minutes}m`;
};

// ============= MAIN COMPONENT =============
const EmployeeMovementPage = () => {
  // State
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedUnitName, setSelectedUnitName] = useState('');
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [todayMovements, setTodayMovements] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sessionChecked, setSessionChecked] = useState(false);
  const [noSession, setNoSession] = useState(false);
  
  // Dialog states
  const [gateOutOpen, setGateOutOpen] = useState(false);
  const [gateInOpen, setGateInOpen] = useState(false);
  const [midwayReturnOpen, setMidwayReturnOpen] = useState(false);
  const [outsideReturnOpen, setOutsideReturnOpen] = useState(false);
  
  // Pending lists
  const [pendingArrivals, setPendingArrivals] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [outsideWorkPending, setOutsideWorkPending] = useState([]);
  
  // Form states
  const [gateOutForm, setGateOutForm] = useState({
    employee_id: '',
    employee_name: '',
    employee_code: '',
    designation: '',
    destination_type: 'inter_unit',
    destination_unit: '',
    outside_details: '',
    purpose: '',
    selfie_base64: ''
  });
  
  const [gateInForm, setGateInForm] = useState({
    movement_id: '',
    selfie_base64: ''
  });
  
  const [midwayForm, setMidwayForm] = useState({
    movement_id: '',
    reason: '',
    selfie_base64: ''
  });
  
  const [outsideReturnForm, setOutsideReturnForm] = useState({
    movement_id: '',
    selfie_base64: ''
  });
  
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ============= DATA FETCHING =============
  
  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API_URL}/units`);
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // Get unit from security check-in session
  const getUnitFromSession = async () => {
    try {
      // First check localStorage for security checkin
      const checkinData = localStorage.getItem('security_checkin');
      if (checkinData) {
        const checkin = JSON.parse(checkinData);
        if (checkin.unit_location_id) {
          // Fetch the unit code from locations API
          const locResponse = await axios.get(`${API_URL.replace('/public/employee-movements', '')}/store/locations-public`);
          const locations = locResponse.data || [];
          const matchedLocation = locations.find(l => l.id === checkin.unit_location_id);
          if (matchedLocation) {
            setSelectedUnit(matchedLocation.code);
            setSelectedUnitName(matchedLocation.name || checkin.unit_location_name);
            setSessionChecked(true);
            return;
          }
        }
      }
      
      // Fallback: check device_id validation
      const deviceId = localStorage.getItem('security_device_id');
      if (deviceId) {
        const validateResponse = await axios.get(
          `${API_URL.replace('/public/employee-movements', '')}/store/security-checkin/validate?device_id=${deviceId}`
        );
        if (validateResponse.data?.valid && validateResponse.data?.checkin) {
          const checkin = validateResponse.data.checkin;
          // Get unit code from locations
          const locResponse = await axios.get(`${API_URL.replace('/public/employee-movements', '')}/store/locations-public`);
          const locations = locResponse.data || [];
          const matchedLocation = locations.find(l => l.id === checkin.unit_location_id);
          if (matchedLocation) {
            setSelectedUnit(matchedLocation.code);
            setSelectedUnitName(matchedLocation.name || checkin.unit_location_name);
            setSessionChecked(true);
            return;
          }
        }
      }
      
      // No valid session found
      setNoSession(true);
      setSessionChecked(true);
    } catch (error) {
      console.error('Error getting unit from session:', error);
      setNoSession(true);
      setSessionChecked(true);
    }
  };

  const fetchEmployees = async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`${API_URL}/employees?${params}`);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTodayMovements = async () => {
    if (!selectedUnit) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/today?unit=${selectedUnit}`);
      setTodayMovements(response.data?.movements || []);
      setStats(response.data?.stats || {});
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingArrivals = async () => {
    if (!selectedUnit) return;
    try {
      const response = await axios.get(`${API_URL}/pending-in?unit=${selectedUnit}`);
      setPendingArrivals(response.data || []);
    } catch (error) {
      console.error('Error fetching pending arrivals:', error);
    }
  };

  const fetchPendingReturns = async () => {
    if (!selectedUnit) return;
    try {
      const response = await axios.get(`${API_URL}/pending-return?unit=${selectedUnit}`);
      setPendingReturns(response.data || []);
    } catch (error) {
      console.error('Error fetching pending returns:', error);
    }
  };

  const fetchOutsideWorkPending = async () => {
    if (!selectedUnit) return;
    try {
      const response = await axios.get(`${API_URL}/outside-work?unit=${selectedUnit}`);
      setOutsideWorkPending(response.data || []);
    } catch (error) {
      console.error('Error fetching outside work:', error);
    }
  };

  const refreshAll = () => {
    fetchTodayMovements();
    fetchPendingArrivals();
    fetchPendingReturns();
    fetchOutsideWorkPending();
  };

  useEffect(() => {
    getUnitFromSession();
    fetchUnits();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedUnit) {
      refreshAll();
    }
  }, [selectedUnit]);

  // ============= FORM HANDLERS =============

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setGateOutForm(prev => ({
        ...prev,
        employee_id: employee.id,
        employee_name: employee.name,
        employee_code: employee.employee_code || '',
        designation: employee.designation || ''
      }));
    }
  };

  const handleGateOutSubmit = async () => {
    if (!gateOutForm.employee_id) {
      alert('Please select an employee');
      return;
    }
    if (!gateOutForm.selfie_base64) {
      alert('Please capture selfie');
      return;
    }
    if (gateOutForm.destination_type === 'inter_unit' && !gateOutForm.destination_unit) {
      alert('Please select destination unit');
      return;
    }
    if (gateOutForm.destination_type === 'outside_work' && !gateOutForm.outside_details) {
      alert('Please enter outside work details');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...gateOutForm,
        source_unit: selectedUnit,
        // Convert free text to uppercase
        outside_details: gateOutForm.outside_details?.toUpperCase() || '',
        purpose: gateOutForm.purpose?.toUpperCase() || ''
      };
      await axios.post(`${API_URL}/gate-out`, payload);
      alert('Gate OUT recorded successfully!');
      setGateOutOpen(false);
      resetGateOutForm();
      refreshAll();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to record Gate OUT');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGateInSubmit = async () => {
    if (!gateInForm.movement_id) {
      alert('Please select a movement');
      return;
    }
    if (!gateInForm.selfie_base64) {
      alert('Please capture selfie');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/gate-in`, gateInForm);
      alert('Gate IN recorded successfully!');
      setGateInOpen(false);
      resetGateInForm();
      refreshAll();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to record Gate IN');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMidwayReturnSubmit = async () => {
    if (!midwayForm.movement_id) {
      alert('Please select a movement');
      return;
    }
    if (!midwayForm.reason) {
      alert('Please enter reason for midway return');
      return;
    }
    if (!midwayForm.selfie_base64) {
      alert('Please capture selfie');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...midwayForm,
        reason: midwayForm.reason?.toUpperCase() || ''
      };
      await axios.post(`${API_URL}/midway-return`, payload);
      alert('Midway return recorded successfully!');
      setMidwayReturnOpen(false);
      resetMidwayForm();
      refreshAll();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to record midway return');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOutsideReturnSubmit = async () => {
    if (!outsideReturnForm.movement_id) {
      alert('Please select a movement');
      return;
    }
    if (!outsideReturnForm.selfie_base64) {
      alert('Please capture selfie');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/outside-return`, outsideReturnForm);
      alert('Outside work return recorded successfully!');
      setOutsideReturnOpen(false);
      resetOutsideReturnForm();
      refreshAll();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to record return');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form functions
  const resetGateOutForm = () => {
    setGateOutForm({
      employee_id: '',
      employee_name: '',
      employee_code: '',
      designation: '',
      destination_type: 'inter_unit',
      destination_unit: '',
      outside_details: '',
      purpose: '',
      selfie_base64: ''
    });
  };

  const resetGateInForm = () => {
    setGateInForm({ movement_id: '', selfie_base64: '' });
    setSelectedMovement(null);
  };

  const resetMidwayForm = () => {
    setMidwayForm({ movement_id: '', reason: '', selfie_base64: '' });
    setSelectedMovement(null);
  };

  const resetOutsideReturnForm = () => {
    setOutsideReturnForm({ movement_id: '', selfie_base64: '' });
    setSelectedMovement(null);
  };

  // ============= RENDER =============

  const filteredEmployees = employees.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.employee_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-muted-foreground">Loading session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to check-in if no valid session
  if (noSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-6 text-center space-y-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-amber-700">Check-In Required</h2>
            <p className="text-muted-foreground">
              You need to complete your security check-in before accessing employee movement.
            </p>
            <Button 
              onClick={() => window.location.href = '/security-checkin'} 
              className="w-full"
            >
              Go to Check-In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Employee Movement</h1>
                <p className="text-sm text-gray-500">Track employee movements between units</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Show current unit from session (read-only) */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  {selectedUnit} - {selectedUnitName}
                </span>
              </div>
              
              <Button variant="outline" onClick={refreshAll} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/security-checkin'}
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <LogOut className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_out || 0}</p>
                  <p className="text-xs text-gray-500">Currently Out</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_completed || 0}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_midway_returned || 0}</p>
                  <p className="text-xs text-gray-500">Midway Returns</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.outside_work_pending || 0}</p>
                  <p className="text-xs text-gray-500">Outside Work</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            onClick={() => { resetGateOutForm(); setGateOutOpen(true); }}
            className="h-20 bg-red-600 hover:bg-red-700 text-white flex flex-col items-center justify-center gap-1"
            data-testid="gate-out-btn"
          >
            <LogOut className="w-6 h-6" />
            <span className="font-semibold">GATE OUT</span>
          </Button>
          
          <Button 
            onClick={() => { resetGateInForm(); fetchPendingArrivals(); setGateInOpen(true); }}
            className="h-20 bg-green-600 hover:bg-green-700 text-white flex flex-col items-center justify-center gap-1"
            data-testid="gate-in-btn"
          >
            <LogIn className="w-6 h-6" />
            <span className="font-semibold">GATE IN</span>
          </Button>
          
          <Button 
            onClick={() => { resetMidwayForm(); fetchPendingReturns(); setMidwayReturnOpen(true); }}
            className="h-20 bg-orange-600 hover:bg-orange-700 text-white flex flex-col items-center justify-center gap-1"
            data-testid="midway-return-btn"
          >
            <RotateCcw className="w-6 h-6" />
            <span className="font-semibold">MIDWAY RETURN</span>
          </Button>
          
          <Button 
            onClick={() => { resetOutsideReturnForm(); fetchOutsideWorkPending(); setOutsideReturnOpen(true); }}
            className="h-20 bg-blue-600 hover:bg-blue-700 text-white flex flex-col items-center justify-center gap-1"
            data-testid="outside-return-btn"
          >
            <Briefcase className="w-6 h-6" />
            <span className="font-semibold">OUTSIDE RETURN</span>
          </Button>
        </div>

        {/* Today's Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Today's Activity
            </CardTitle>
            <CardDescription>All movements for {selectedUnit || 'selected unit'} today</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : todayMovements.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No movements recorded today</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {todayMovements.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="text-sm">
                          <div>{formatTime(m.out_at)}</div>
                          {m.in_at && (
                            <div className="text-xs text-gray-500">→ {formatTime(m.in_at)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{m.employee_name}</div>
                          <div className="text-xs text-gray-500">{m.employee_code}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{m.source_unit}</Badge>
                        </TableCell>
                        <TableCell>
                          {m.destination_type === 'outside_work' ? (
                            <div className="text-sm">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {m.outside_details?.substring(0, 20)}...
                            </div>
                          ) : (
                            <Badge variant="outline">{m.destination_unit || '-'}</Badge>
                          )}
                        </TableCell>
                        <TableCell>{formatDuration(m.duration_mins)}</TableCell>
                        <TableCell><StatusBadge status={m.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ============= GATE OUT DIALOG ============= */}
      <Dialog open={gateOutOpen} onOpenChange={setGateOutOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-red-600" />
              Employee Gate OUT
            </DialogTitle>
            <DialogDescription>
              Record employee leaving from {selectedUnit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Employee Search */}
            <div className="space-y-2">
              <Label>Employee *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="employee-search"
                />
              </div>
              {searchTerm && filteredEmployees.length > 0 && !gateOutForm.employee_id && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {filteredEmployees.slice(0, 10).map(emp => (
                    <div
                      key={emp.id}
                      className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => { handleEmployeeSelect(emp.id); setSearchTerm(''); }}
                    >
                      <div className="font-medium">{emp.name}</div>
                      <div className="text-xs text-gray-500">{emp.employee_code} | {emp.designation}</div>
                    </div>
                  ))}
                </div>
              )}
              {gateOutForm.employee_id && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="font-medium">{gateOutForm.employee_name}</div>
                  <div className="text-sm text-gray-600">
                    {gateOutForm.employee_code} | {gateOutForm.designation}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-1 text-xs"
                    onClick={() => { resetGateOutForm(); setSearchTerm(''); }}
                  >
                    Change
                  </Button>
                </div>
              )}
            </div>

            {/* Destination Type */}
            <div className="space-y-2">
              <Label>Going To *</Label>
              <RadioGroup
                value={gateOutForm.destination_type}
                onValueChange={(val) => setGateOutForm(prev => ({ 
                  ...prev, 
                  destination_type: val,
                  destination_unit: '',
                  outside_details: ''
                }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="inter_unit" id="inter_unit" />
                  <Label htmlFor="inter_unit" className="cursor-pointer">Inter-Unit</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="outside_work" id="outside_work" />
                  <Label htmlFor="outside_work" className="cursor-pointer">Outside Work</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Destination Unit (for inter-unit) */}
            {gateOutForm.destination_type === 'inter_unit' && (
              <div className="space-y-2">
                <Label>Destination Unit *</Label>
                <Select 
                  value={gateOutForm.destination_unit} 
                  onValueChange={(val) => setGateOutForm(prev => ({ ...prev, destination_unit: val }))}
                >
                  <SelectTrigger data-testid="destination-unit">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.filter(u => u.code !== selectedUnit).map(unit => (
                      <SelectItem key={unit.id} value={unit.code}>
                        {unit.code} - {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Outside Details (for outside work) */}
            {gateOutForm.destination_type === 'outside_work' && (
              <div className="space-y-2">
                <Label>Outside Work Details *</Label>
                <Textarea
                  placeholder="e.g., Client visit - ABC Corp, Bank work, etc."
                  value={gateOutForm.outside_details}
                  onChange={(e) => setGateOutForm(prev => ({ ...prev, outside_details: e.target.value }))}
                  data-testid="outside-details"
                />
              </div>
            )}

            {/* Purpose (optional) */}
            <div className="space-y-2">
              <Label>Purpose (Optional)</Label>
              <Input
                placeholder="Reason for visit..."
                value={gateOutForm.purpose}
                onChange={(e) => setGateOutForm(prev => ({ ...prev, purpose: e.target.value }))}
              />
            </div>

            {/* Selfie Capture */}
            <div className="space-y-2">
              <Label>Employee Selfie *</Label>
              <CameraCapture
                onCapture={(img) => setGateOutForm(prev => ({ ...prev, selfie_base64: img }))}
                capturedImage={gateOutForm.selfie_base64}
                onRetake={() => setGateOutForm(prev => ({ ...prev, selfie_base64: '' }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGateOutOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleGateOutSubmit} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-gate-out"
            >
              {submitting ? 'Recording...' : 'Confirm Gate OUT'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= GATE IN DIALOG ============= */}
      <Dialog open={gateInOpen} onOpenChange={setGateInOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="w-5 h-5 text-green-600" />
              Employee Gate IN
            </DialogTitle>
            <DialogDescription>
              Record employee arriving at {selectedUnit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Pending Arrivals List */}
            <div className="space-y-2">
              <Label>Select Arriving Employee *</Label>
              {pendingArrivals.length === 0 ? (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No pending arrivals at this unit</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pendingArrivals.map(m => (
                    <div
                      key={m.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        gateInForm.movement_id === m.id 
                          ? 'border-green-500 bg-green-50' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedMovement(m);
                        setGateInForm(prev => ({ ...prev, movement_id: m.id }));
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{m.employee_name}</div>
                          <div className="text-xs text-gray-500">
                            From: {m.source_unit} | Out: {formatTime(m.out_at)}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {m.mins_since_out}m ago
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selfie Capture */}
            <div className="space-y-2">
              <Label>Employee Selfie *</Label>
              <CameraCapture
                onCapture={(img) => setGateInForm(prev => ({ ...prev, selfie_base64: img }))}
                capturedImage={gateInForm.selfie_base64}
                onRetake={() => setGateInForm(prev => ({ ...prev, selfie_base64: '' }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGateInOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleGateInSubmit} 
              disabled={submitting || !gateInForm.movement_id}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-gate-in"
            >
              {submitting ? 'Recording...' : 'Confirm Gate IN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= MIDWAY RETURN DIALOG ============= */}
      <Dialog open={midwayReturnOpen} onOpenChange={setMidwayReturnOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              Midway Return
            </DialogTitle>
            <DialogDescription>
              Record employee returning to {selectedUnit} without reaching destination
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Pending Returns List */}
            <div className="space-y-2">
              <Label>Select Returning Employee *</Label>
              {pendingReturns.length === 0 ? (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No employees currently out from this unit</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pendingReturns.filter(m => m.destination_type === 'inter_unit').map(m => (
                    <div
                      key={m.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        midwayForm.movement_id === m.id 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedMovement(m);
                        setMidwayForm(prev => ({ ...prev, movement_id: m.id }));
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{m.employee_name}</div>
                          <div className="text-xs text-gray-500">
                            Going to: {m.destination_unit} | Out: {formatTime(m.out_at)}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {m.mins_since_out}m ago
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason for Midway Return *</Label>
              <Select 
                value={midwayForm.reason} 
                onValueChange={(val) => setMidwayForm(prev => ({ ...prev, reason: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vehicle breakdown">Vehicle breakdown</SelectItem>
                  <SelectItem value="Called back by supervisor">Called back by supervisor</SelectItem>
                  <SelectItem value="Personal emergency">Personal emergency</SelectItem>
                  <SelectItem value="Destination closed">Destination closed</SelectItem>
                  <SelectItem value="Weather conditions">Weather conditions</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {midwayForm.reason === 'Other' && (
                <Input
                  placeholder="Specify reason..."
                  onChange={(e) => setMidwayForm(prev => ({ ...prev, reason: e.target.value.toUpperCase() }))}
                />
              )}
            </div>

            {/* Selfie Capture */}
            <div className="space-y-2">
              <Label>Employee Selfie *</Label>
              <CameraCapture
                onCapture={(img) => setMidwayForm(prev => ({ ...prev, selfie_base64: img }))}
                capturedImage={midwayForm.selfie_base64}
                onRetake={() => setMidwayForm(prev => ({ ...prev, selfie_base64: '' }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMidwayReturnOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleMidwayReturnSubmit} 
              disabled={submitting || !midwayForm.movement_id}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-midway-return"
            >
              {submitting ? 'Recording...' : 'Confirm Midway Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= OUTSIDE RETURN DIALOG ============= */}
      <Dialog open={outsideReturnOpen} onOpenChange={setOutsideReturnOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Outside Work Return
            </DialogTitle>
            <DialogDescription>
              Record employee returning from outside work to {selectedUnit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Outside Work Pending List */}
            <div className="space-y-2">
              <Label>Select Returning Employee *</Label>
              {outsideWorkPending.length === 0 ? (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No employees currently out for outside work</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {outsideWorkPending.map(m => (
                    <div
                      key={m.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        outsideReturnForm.movement_id === m.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedMovement(m);
                        setOutsideReturnForm(prev => ({ ...prev, movement_id: m.id }));
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{m.employee_name}</div>
                          <div className="text-xs text-gray-500">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {m.outside_details}
                          </div>
                          <div className="text-xs text-gray-400">
                            Out: {formatTime(m.out_at)}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {m.mins_since_out}m ago
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selfie Capture */}
            <div className="space-y-2">
              <Label>Employee Selfie *</Label>
              <CameraCapture
                onCapture={(img) => setOutsideReturnForm(prev => ({ ...prev, selfie_base64: img }))}
                capturedImage={outsideReturnForm.selfie_base64}
                onRetake={() => setOutsideReturnForm(prev => ({ ...prev, selfie_base64: '' }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOutsideReturnOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleOutsideReturnSubmit} 
              disabled={submitting || !outsideReturnForm.movement_id}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-outside-return"
            >
              {submitting ? 'Recording...' : 'Confirm Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeMovementPage;
